import { useAtomValue, useSetAtom } from "jotai";
import { type ChangeEvent, useCallback, useEffect } from "react";
import { widgetMenuOptionsAtom, widgetMenuOptionsByNameAtom } from "@/interface/AppStore";
import { applyValue, setNewOptions } from "../../lib";
import type { ActiveSubMenuState, ColorItem, SetActiveSubMenu } from "../../types";
import { SubMenu } from "../SubMenu/SubMenu";
import { ColorMenuContent } from "./ColorMenuContent";

interface ColorMenuProps {
    activeSubMenu: ActiveSubMenuState;
    setActiveSubMenu: SetActiveSubMenu;
}

export function ColorMenu({ activeSubMenu, setActiveSubMenu }: ColorMenuProps) {
    const setWidgetMenuOptions = useSetAtom(widgetMenuOptionsAtom);
    const colorOptions = useAtomValue(widgetMenuOptionsByNameAtom(activeSubMenu.name));

    const handleColorButtonClick = useCallback(
        (item: ColorItem) => {
            if (item.isPicker) {
                return;
            }

            const setNewColorOptions = setNewOptions({
                name: activeSubMenu.name,
                setWidgetMenuOptions,
            });

            function handleClose() {
                setActiveSubMenu(null);
            }

            applyValue({
                options: colorOptions,
                value: item.color,
                setNewOptions: setNewColorOptions,
                handleClose,
            });
        },
        [activeSubMenu, colorOptions, setActiveSubMenu, setWidgetMenuOptions],
    );

    const handleColorInputChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const value = event.currentTarget.value;

            const setNewColorOptions = setNewOptions({
                name: activeSubMenu.name,
                setWidgetMenuOptions,
            });

            applyValue({
                options: colorOptions,
                value,
                setNewOptions: setNewColorOptions,
            });
        },
        [activeSubMenu, colorOptions, setWidgetMenuOptions],
    );

    useEffect(() => {
        if (!colorOptions?.length) {
            setActiveSubMenu(null);
        }
    }, [colorOptions, setActiveSubMenu]);

    if (!colorOptions?.length) {
        return null;
    }

    return (
        <SubMenu padding={8} activeSubMenu={activeSubMenu}>
            <ColorMenuContent
                colorValue={colorOptions[0].value as string}
                onColorClick={handleColorButtonClick}
                onColorInputChange={handleColorInputChange}
            />
        </SubMenu>
    );
}
